import React, { useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import { GridOptions, ColDef } from "ag-grid-enterprise";
import { CellValueChangedEvent, ICellRendererParams } from "ag-grid-community";
import "ag-grid-enterprise";

// CSS imports for AgGrid
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import "./MachineTransfer.css";

import apiAxios from "../../utils/apiAxios";
import Spinner from "../ui/Spinner";
import { appendAlert } from "../../redux/alertSlice";
import { useDispatch } from "react-redux";
import IconHeaderComponent from "../../components/grid/IconHeaderComponent";

interface MachineTransferProps {
  onClose: () => void;
  selectedRows: any[];
}

// **Define interfaces for your data types**
interface JobData {
  seq: number;
  po_desc_num: string;
  mach_code: string;
  qty: number;
  cut_long: number;
  cut_short: number;
  po_long: number;
  po_short: number;
  thickness: number;
  edge_proc_code_long: string;
  edge_proc_code_short: string;
  conveyor_speed: number;
  corner_rounding: number;
  corner_rounding_left_front: number;
  corner_rounding_right_rear: number;
  corner_rounding_right_front: number;
  corner_rounding_left_rear: number;
  bottom_arrising: number;
  top_arrising: number;
  distance_between_glasses: number;
  has_mark: number;
  marking_duration: number;
  inspection_duration: number;
  handling_time: number;
  status: string;
  completion_datetime: string | null;
  creation_datetime: string;
  update_datetime: string;
  destin_name: string;
  customer_name: string;
}

interface TransferRowData {
  sequence?: number;
  dragHandle?: any;
  po_desc_num: string;
  proc_width: number;
  proc_height: number;
  po_width: number;
  po_height: number;
  current_qty: number;
  edge_finishes: string;
  edge_proc_code_long: string;
  edge_proc_code_short: string;
  destin_name: string;
  customer_name: string;
  mach_code: string;
  thickness: number;
  marks?: string; // マーク情報を追加
  [key: string]: any; // インデックスシグネチャを追加
}

// DeleteButton を関数コンポーネントの外に移動
const DeleteButton: React.FC<ICellRendererParams> = (props) => {
  const handleDelete = async (po_desc_num: string) => {
    try {
      await apiAxios.delete(`/b_wedger/118/jobs/${po_desc_num}`);
      props.api.applyTransaction({ remove: [props.data] });
    } catch (error) {
      console.error("Failed to delete data:", error);
    }
  };

  return (
    <button
      onClick={() => handleDelete(props.data.po_desc_num)}
      className="ag-icon-button"
      style={{ border: "none", background: "none", cursor: "pointer" }}
    >
      <i className="fas fa-trash" style={{ color: "#FF4136" }}></i>
    </button>
  );
};

const MachineTransfer: React.FC<MachineTransferProps> = ({
  onClose,
  selectedRows,
}) => {
  // Initialize edge_proc_code_long and edge_proc_code_short
  const initialRowData: TransferRowData[] = selectedRows.map((row) => {
    const edgeProcCode = row.edge_finishes === "糸" ? "F-A" : "";
    return {
      ...row,
      edge_proc_code_long: edgeProcCode,
      edge_proc_code_short: edgeProcCode,
      current_qty: row.current_qty ?? 1, // Ensure quantity is set
    };
  });
  const [rowData, setRowData] = useState<TransferRowData[]>(initialRowData);

  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const gridOptions: GridOptions<TransferRowData> = {
    defaultColDef: {
      resizable: true,
      sortable: true,
      filter: true,
    },
    rowDragManaged: true, // Enable row drag management
    onRowDragEnd: (event) => {
      // After dragging ends, refresh cells to update sequence numbers
      event.api.refreshCells({
        columns: ["sequence"],
        force: true,
      });
    },
  };

  const columnDefs: ColDef<TransferRowData>[] = [
    {
      headerName: "#",
      field: "sequence",
      valueGetter: "node.rowIndex + 1",
      width: 50,
    },
    {
      headerName: "",
      field: "dragHandle",
      width: 50,
      rowDrag: true,
    },
    {
      headerName: "受注NO",
      field: "po_desc_num",
      width: 130,
    },
    {
      headerName: "切長辺",
      width: 80,
      valueGetter: (params) => {
        const width = Number(params.data?.proc_width);
        const height = Number(params.data?.proc_height);
        return Math.max(width, height);
      },
    },
    {
      headerName: "切短辺",
      width: 80,
      valueGetter: (params) => {
        const width = Number(params.data?.proc_width);
        const height = Number(params.data?.proc_height);
        return Math.min(width, height);
      },
    },
    { headerName: "仕上W", field: "po_width", width: 80 },
    { headerName: "仕上H", field: "po_height", width: 80 },
    {
      headerName: "数量(編集可)",
      field: "current_qty",
      width: 120,
      editable: true,
      cellClass: "editable-cell",
    },
    { headerName: "小口加工", field: "edge_finishes", width: 120 },
    {
      headerName: "長辺加工(編集可)",
      field: "edge_proc_code_long",
      width: 120,
      editable: true,
      cellEditor: "agRichSelectCellEditor",
      cellEditorParams: {
        values: ["F-A", "F-G", "F-P", "R-G", "R-P"],
      },
    },
    {
      headerName: "短辺加工(編集可)",
      field: "edge_proc_code_short",
      width: 120,
      editable: true,
      cellEditor: "agRichSelectCellEditor",
      cellEditorParams: {
        values: ["F-A", "F-G", "F-P", "R-G", "R-P"],
      },
    },
    { headerName: "送り先名", field: "destin_name", width: 140 },
    { headerName: "得意先名", field: "customer_name", width: 140 },
    {
      headerName: "マーク有無",
      field: "marks",
      width: 100,
      cellRenderer: "agCheckboxCellRenderer",
      cellRendererParams: {
        checkbox: true,
      },
      cellStyle: { textAlign: "center" },
    },
  ];

  const handleCellValueChanged = (
    event: CellValueChangedEvent<TransferRowData>
  ) => {
    const updatedData: TransferRowData[] = [...rowData];
    const field = event.colDef.field as keyof TransferRowData;
    if (field && event.rowIndex !== null) {
      if (field === "marks") {
        updatedData[event.rowIndex][field] = event.newValue ? "true" : "";
      } else {
        updatedData[event.rowIndex][field] = event.newValue;
      }
      setRowData(updatedData);
    }
  };

  // Compute invalid rows
  const invalidRows = rowData.filter(
    (row) => !row.edge_proc_code_long || !row.edge_proc_code_short
  );
  const isValid = invalidRows.length === 0;

  const handleTransfer = async () => {
    if (!isValid) return;

    setIsLoading(true);
    const transferData = rowData.map((row) => ({
      po_desc_num: row.po_desc_num,
      mach_code: row.mach_code,
      qty: row.current_qty,
      cut_long: Math.max(Number(row.proc_width), Number(row.proc_height)),
      cut_short: Math.min(Number(row.proc_width), Number(row.proc_height)),
      po_long: Math.max(Number(row.po_width), Number(row.po_height)),
      po_short: Math.min(Number(row.po_width), Number(row.po_height)),
      thickness: row.thickness,
      edge_proc_code_long: row.edge_proc_code_long,
      edge_proc_code_short: row.edge_proc_code_short,
      corner_rounding: 1,
      corner_rounding_left_front: 1.0,
      corner_rounding_right_rear: 1.0,
      corner_rounding_right_front: 1.0,
      corner_rounding_left_rear: 10.0,
      bottom_arrising: 0.5,
      top_arrising: 0.5,
      has_mark: row.marks ? 1 : 0,
      status: "ready",
      destin_name: row.destin_name,
      customer_name: row.customer_name,
    }));

    try {
      const responses = await Promise.all(
        transferData.map((data) => apiAxios.post("/b_wedger/118/jobs", data))
      );
      console.log("Transfer successful:", responses);
      dispatch(
        appendAlert({
          severity: "success",
          message: "機械への転送が完了しました。",
          displayTime: 5000,
        })
      );
      onClose(); // Close the modal after successful transfer
    } catch (error) {
      console.error("Transfer failed:", error);
      dispatch(
        appendAlert({
          severity: "error",
          message: "機械への転送に失敗しました。",
          displayTime: 5000,
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  // State to hold fetched data
  const [fetchedData, setFetchedData] = useState<JobData[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  // Fetch data from the endpoint
  useEffect(() => {
    const fetchData = async () => {
      setIsFetching(true);
      try {
        const response = await apiAxios.get<JobData[]>("/b_wedger/118/jobs");
        setFetchedData(response.data);
      } catch (error) {
        console.error("Failed to fetch data:", error);
        dispatch(
          appendAlert({
            severity: "error",
            message: "データの取得に失敗しました。",
            displayTime: 5000,
          })
        );
      } finally {
        setIsFetching(false);
      }
    };

    fetchData();
  }, [dispatch]);

  // Column definitions for the fetched data grid
  const fetchedColumnDefs: ColDef<JobData>[] = [
    {
      headerName: "操作",
      width: 70,
      cellRenderer: DeleteButton,
      headerComponent: IconHeaderComponent,
      headerComponentParams: {
        iconClasses: ["fas fa-trash"],
        iconColor: "#FF4136",
      },
    },
    { headerName: "Seq", field: "seq", width: 70 },
    { headerName: "受注NO", field: "po_desc_num", width: 130 },
    { headerName: "機械コード", field: "mach_code", width: 100 },
    { headerName: "数量", field: "qty", width: 80 },
    { headerName: "切長辺", field: "cut_long", width: 100 },
    { headerName: "切短辺", field: "cut_short", width: 100 },
    { headerName: "仕上長辺", field: "po_long", width: 100 },
    { headerName: "仕上短辺", field: "po_short", width: 100 },
    { headerName: "厚み", field: "thickness", width: 80 },
    { headerName: "長辺加工", field: "edge_proc_code_long", width: 150 },
    { headerName: "短辺加工", field: "edge_proc_code_short", width: 150 },
    { headerName: "ステータス", field: "status", width: 100 },
    {
      headerName: "完了日時",
      field: "completion_datetime",
      width: 180,
    },
    { headerName: "送り先名", field: "destin_name", width: 150 },
    { headerName: "得意先名", field: "customer_name", width: 150 },

    // Add other fields as needed
  ];

  // データ削除後の再取得ロジック
  const refreshData = async () => {
    try {
      const response = await apiAxios.get<JobData[]>("/b_wedger/118/jobs");
      setFetchedData(response.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      dispatch(
        appendAlert({
          severity: "error",
          message: "データの取得に失敗しました。",
          displayTime: 5000,
        })
      );
    }
  };

  // コンポーネントがマウントされたときにデータを取得
  useEffect(() => {
    refreshData();
  }, []);

  return (
    <div className="machine-transfer-container">
      {isLoading && <Spinner />}
      <h2>機械転送リスト</h2>
      <div
        className="ag-theme-balham"
        style={{ height: "400px", width: "100%" }}
      >
        <AgGridReact<TransferRowData>
          gridOptions={gridOptions}
          columnDefs={columnDefs}
          rowData={rowData}
          rowSelection="multiple"
          onCellValueChanged={handleCellValueChanged}
        />
      </div>

      <div className="machine-transfer-actions">
        {!isValid && (
          <div style={{ color: "red", marginBottom: "10px" }}>
            以下の受注NOの長辺加工と短辺加工を指定してください。:{" "}
            {invalidRows.map((row) => row.po_desc_num).join(", ")}
          </div>
        )}
        <button onClick={onClose} autoFocus>
          キャンセル
        </button>
        <button onClick={handleTransfer} disabled={!isValid}>
          機械に転送
        </button>
      </div>

      {/* Added: Display the fetched data using AgGrid */}
      <h2>機械転送キュー</h2>
      {isFetching ? (
        <Spinner />
      ) : (
        <div
          className="ag-theme-balham"
          style={{ height: "400px", width: "100%" }}
        >
          <AgGridReact<JobData>
            columnDefs={fetchedColumnDefs}
            rowData={fetchedData}
          />
        </div>
      )}
    </div>
  );
};

export default MachineTransfer;
